const FEE_KEYS = ['Fees', 'fees', 'Fee', 'fee', 'CardFees', 'cardFees', 'CustomerFee', 'customerFee'];

export function readMoney(source, ...keys) {
    if (!source) return 0;
    for (let i = 0; i < keys.length; i += 1) {
        const value = source[keys[i]];
        if (value === null || value === undefined || value === '') continue;
        const numeric = typeof value === 'string'
            ? parseFloat(String(value).replace(/[^0-9.-]/g, ''))
            : parseFloat(value);
        if (Number.isFinite(numeric)) return numeric;
    }
    return 0;
}

export function roundMoney(value) {
    return Math.round((Number(value) || 0) * 100) / 100;
}

function readExcludeFeesFlag(source) {
    if (!source) return false;
    const raw = source.CardSalesExcludeFees ?? source.cardSalesExcludeFees;
    if (raw === true || raw === 1) return true;
    if (typeof raw === 'string') {
        const normalized = raw.trim().toLowerCase();
        return normalized === 'true' || normalized === '1';
    }
    return false;
}

/**
 * Prefer merch-only Card/Member when the API sets CardSalesExcludeFees
 * (Fees listed separately). Legacy rows bake fee into CardSales — peel Fees
 * off card first, then member, so totals stay food and drink.
 */
export function salesAmounts(source) {
    const cash = readMoney(source, 'CashSales', 'cashSales');
    let card = readMoney(source, 'CardSales', 'cardSales');
    let member = readMoney(source, 'MemberSales', 'memberSales');
    const fees = readMoney(source, ...FEE_KEYS);
    const alreadyExcluded = readExcludeFeesFlag(source);

    if (!alreadyExcluded && fees > 0) {
        let remaining = fees;
        if (card > 0) {
            const take = Math.min(card, remaining);
            card = roundMoney(card - take);
            remaining = roundMoney(remaining - take);
        }
        if (remaining > 0 && member > 0) {
            const take = Math.min(member, remaining);
            member = roundMoney(member - take);
        }
    }

    return {
        cash,
        card,
        member,
        fees,
        total: roundMoney(cash + card + member),
    };
}

/**
 * Additive reporting nets (v3.1). Prefer API fields; fall back so older
 * payloads still show before-refund totals without inventing refund math.
 */
export function reportingNets(source) {
    const base = salesAmounts(source);
    const salesBeforeRefunds = readMoney(
        source,
        'SalesBeforeRefunds',
        'salesBeforeRefunds',
    ) || base.total;
    const merchRefunds = readMoney(source, 'MerchRefunds', 'merchRefunds');
    const hasNetField = source
        && (source.NetSalesMerch != null || source.netSalesMerch != null);
    const netSalesMerch = hasNetField
        ? readMoney(source, 'NetSalesMerch', 'netSalesMerch')
        : roundMoney(Math.max(0, salesBeforeRefunds - merchRefunds));

    return {
        ...base,
        salesBeforeRefunds,
        menuGross: readMoney(source, 'MenuGross', 'menuGross'),
        discounts: readMoney(source, 'Discounts', 'discounts'),
        comps: readMoney(source, 'Comps', 'comps'),
        merchRefunds,
        netSalesMerch,
        customerRefunds: readMoney(source, 'CustomerRefunds', 'customerRefunds'),
        taxCollected: readMoney(source, 'TaxCollected', 'taxCollected'),
        taxRefunded: readMoney(source, 'TaxRefunded', 'taxRefunded'),
        taxNet: readMoney(source, 'TaxNet', 'taxNet'),
        netCashSales: readMoney(source, 'NetCashSales', 'netCashSales'),
        netCardSales: readMoney(source, 'NetCardSales', 'netCardSales'),
        netMemberSales: readMoney(source, 'NetMemberSales', 'netMemberSales'),
        tipRefundedDisplay: readMoney(source, 'TipRefundedDisplay', 'tipRefundedDisplay'),
        netTipsDisplay: readMoney(source, 'NetTipsDisplay', 'netTipsDisplay'),
        gratuityRefundedDisplay: readMoney(source, 'GratuityRefundedDisplay', 'gratuityRefundedDisplay'),
        netGratuityDisplay: readMoney(source, 'NetGratuityDisplay', 'netGratuityDisplay'),
        feeRefundedDisplay: readMoney(source, 'FeeRefundedDisplay', 'feeRefundedDisplay'),
        netFeeDisplay: readMoney(source, 'NetFeeDisplay', 'netFeeDisplay'),
        merchRefundEstimated: !!(source?.MerchRefundEstimated ?? source?.merchRefundEstimated),
        tipGratRefundEstimated: !!(source?.TipGratRefundEstimated ?? source?.tipGratRefundEstimated),
        merchRefundBasis: source?.MerchRefundBasis ?? source?.merchRefundBasis ?? null,
    };
}

export function hasRefundReporting(nets) {
    if (!nets) return false;
    // Only when there is at least one non-zero card to show.
    return nets.merchRefunds > 0
        || nets.discounts > 0
        || nets.comps > 0
        || nets.customerRefunds > 0
        || nets.tipRefundedDisplay > 0
        || nets.gratuityRefundedDisplay > 0;
}
